import React, { useState } from "react";

import {
  HelpCircleIcon,
  LogOutIcon,
  MoonIcon,
  ShieldIcon,
  SunIcon,
} from "lucide-react";

import { useTheme } from "@/context/ThemeContext";

import { RecordingNavWarningModal } from "@components/RecordingDock/RecordingNavWarningModal";
import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";
import { Badge } from "@components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { DEVELOPMENT_ENV } from "@constants/common";

import { useAuth } from "@hooks/useAuth";

import { getEnv } from "@utils/env";
import { cn } from "@utils/ui";

const isDev = getEnv("VITE_ENV") === DEVELOPMENT_ENV;

interface UserCommandPopoverProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const UserCommandPopover: React.FC<UserCommandPopoverProps> = ({
  children,
  open,
  onOpenChange,
}) => {
  const { logoutWithNavigation } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  // Logging out ends any active recording: warn first, and
  // stop + save the session before the sign-out redirect if the user proceeds.
  const recordingExamineeName = useRecordingDock((s) => s.examineeName);
  const [logoutWarnOpen, setLogoutWarnOpen] = useState(false);

  const handleSelectItem = (action: string) => {
    console.log(`Selected action: ${action}`);
    onOpenChange?.(false);
  };

  const doLogout = () => {
    logoutWithNavigation();
    onOpenChange?.(false);
  };

  const onLogout = () => {
    if (useRecordingDock.getState().isActive()) {
      setLogoutWarnOpen(true);
      return;
    }
    doLogout();
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="m-2 w-64 space-y-1 border-none bg-background-white px-2 py-4 drop-shadow-xl"
        side="right"
        align="end"
      >
        <PopoverButton
          icon={<ShieldIcon className="h-4 w-4" />}
          label="Terms & Policy"
          onClick={() => handleSelectItem("terms")}
        />
        <PopoverButton
          icon={<HelpCircleIcon className="h-4 w-4" />}
          label="Help"
          onClick={() => handleSelectItem("help")}
        />
        {isDev && (
          <PopoverButton
            icon={
              theme === "dark" ? (
                <SunIcon className="h-4 w-4" />
              ) : (
                <MoonIcon className="h-4 w-4" />
              )
            }
            label={theme === "dark" ? "Light Mode" : "Dark Mode"}
            badge={
              <Badge variant="accent" className="px-3 py-0 text-[0.6rem]">
                Dev Mode
              </Badge>
            }
            onClick={toggleTheme}
          />
        )}

        <figure className="my-2 h-[1px] w-full bg-border" />

        <PopoverButton
          icon={<LogOutIcon className="h-4 w-4" />}
          label="Log Out"
          onClick={onLogout}
        />
      </PopoverContent>

      <RecordingNavWarningModal
        isOpen={logoutWarnOpen}
        examineeName={recordingExamineeName}
        onStay={() => setLogoutWarnOpen(false)}
        onFinishAndLeave={() => {
          useRecordingDock.getState().controls.stop();
          setLogoutWarnOpen(false);
          doLogout();
        }}
      />
    </Popover>
  );
};

type PopoverButtonProps = {
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
  onClick: () => void;
};
const PopoverButton = ({ icon, label, badge, onClick }: PopoverButtonProps) => {
  return (
    <button
      className={cn(
        "relative grid w-full grid-cols-[auto_1fr] items-center gap-4 rounded-2xl bg-transparent px-4 py-3",
        "border border-background/0",
        "text-left text-base font-medium",
        "cursor-pointer transition-all duration-200",
        "hover:border-accent hover:bg-accent-50"
      )}
      onClick={onClick}
    >
      {icon}
      <span className="flex items-center gap-2">
        {label}
        {badge}
      </span>
    </button>
  );
};
